import React, { useRef } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileText, ShieldCheck, Upload, Trash2, Image as ImageIcon } from "lucide-react";

const upload = async (url, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
};

export const PhotoField = ({ profile, setProfile, base }) => {
  const ref = useRef();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { data } = await upload(`${base}/photo`, file);
      setProfile((p) => ({ ...p, photo_url: data.photo_url }));
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-4" data-testid="profile-photo-section">
      <div className="w-20 h-20 rounded-full overflow-hidden bg-[#2D6A4F]/10 border border-[#1E3F33]/10 flex items-center justify-center shrink-0">
        {profile.photo_url ? (
          <img src={profile.photo_url.startsWith("http") ? profile.photo_url : `${API}/files/${profile.photo_url}`} alt={profile.business_name || "Profile"} className="w-full h-full object-cover" data-testid="profile-photo-preview" />
        ) : profile.company_logo_url ? (
          <img src={`${API}/files/${profile.company_logo_url}`} alt="Company logo" className="w-full h-full object-contain p-2 bg-white" data-testid="profile-photo-preview" />
        ) : <ImageIcon className="w-7 h-7 text-[#8A9A86]" />}
      </div>
      <div>
        <label className="label-soft">Profile photo <span className="text-[#8A9A86] font-normal">(optional)</span></label>
        {!profile.photo_url && profile.company_logo_url && <p className="text-xs text-[#2D6A4F] mb-1" data-testid="profile-photo-logo-note">Using your company logo until a photo is added.</p>}
        <p className="text-xs text-[#8A9A86] mb-2">Shown on your collector profile. JPG, PNG or WEBP, up to 8MB.</p>
        <input ref={ref} data-testid="profile-photo-input" type="file" accept="image/*" onChange={onFile} className="hidden" />
        <button type="button" data-testid="profile-photo-button" onClick={() => ref.current?.click()} className="btn-secondary inline-flex items-center gap-2">
          <Upload className="w-4 h-4" /> {profile.photo_url ? "Change photo" : "Upload photo"}
        </button>
      </div>
    </div>
  );
};

const CertStatus = ({ c, i }) => {
  const st = c.verification_status || "pending";
  const cls = st === "verified" ? "bg-[#2D6A4F]/12 text-[#2D6A4F]" : st === "rejected" ? "bg-[#CC5A3A]/12 text-[#CC5A3A]" : "bg-[#8A9A86]/15 text-[#5C6B64]";
  return (
    <div className="flex items-center gap-2 flex-wrap mt-1.5">
      <span data-testid={`cert-status-${i}`} className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${cls}`}>
        {st === "verified" ? "Verified" : st === "rejected" ? "Rejected" : "Pending review"}
      </span>
      {(c.matched_keywords || []).slice(0, 4).map((kw, j) => (
        <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-[#2D6A4F]/8 text-[#2D6A4F]">{kw}</span>
      ))}
    </div>
  );
};

export const CertificationsCard = ({ profile, base, reload }) => {
  const ref = useRef();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await upload(`${base}/certifications`, file);
      toast.success("Certification uploaded");
      await reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { if (ref.current) ref.current.value = ""; }
  };
  const remove = async (path) => {
    try { await api.delete(`${base}/certifications`, { params: { file_path: path } }); await reload(); }
    catch { toast.error("Delete failed"); }
  };
  const certs = profile.certifications || [];
  return (
    <div className="card-soft" data-testid="certifications-card">
      <h2 className="font-heading text-xl text-[#1E3F33] mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2D6A4F]"/> Certifications</h2>
      <p className="text-sm text-[#5C6B64] mb-3">Upload licenses &amp; certifications (PDF, JPG, PNG). We scan each document and auto-verify it when recognized certification terms are found.</p>
      <input ref={ref} data-testid="cert-upload-input" type="file" accept="application/pdf,image/*" onChange={onFile} className="hidden"/>
      <button type="button" data-testid="cert-upload-button" onClick={() => ref.current?.click()} className="btn-secondary w-full inline-flex items-center justify-center gap-2">
        <Upload className="w-4 h-4"/> Upload document
      </button>
      <ul className="mt-4 space-y-3">
        {certs.map((c, i) => (
          <li key={i} className="text-sm border border-[#1E3F33]/10 rounded-lg p-2.5" data-testid={`cert-item-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <a href={`${API}/files/${c.file_path}`} target="_blank" rel="noreferrer" className="text-[#CC5A3A] hover:underline inline-flex items-center gap-1.5 min-w-0">
                <FileText className="w-4 h-4 shrink-0"/> <span className="truncate">{c.original_name}</span>
              </a>
              <button type="button" data-testid={`cert-delete-${i}`} onClick={() => remove(c.file_path)} className="text-[#8A9A86] hover:text-[#E63946] shrink-0"><Trash2 className="w-4 h-4"/></button>
            </div>
            <CertStatus c={c} i={i} />
          </li>
        ))}
        {certs.length === 0 && <li className="text-sm text-[#8A9A86]">No documents yet.</li>}
      </ul>
    </div>
  );
};
